// Вкладка «Все отчёты»: отчёты всех пользователей (id «u<uid>:имя»), поиск, фильтры по владельцу и виду работы,
// сортировка; открыть и редактировать (в редакторе - плашка «чужой отчёт»), скачать архив, в корзину;
// корзины пользователей - восстановить или удалить навсегда (с подтверждением).
import { useEffect, useMemo, useState } from 'react';
import { ctx } from '../../core/ctx';
import { askConfirm } from '../../ui/prompts';
import { Btn, Segmented } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { TYPE_NAMES } from '../../ui/StartScreen';
import { download } from '../shell/file';
import { Avatar } from '../auth/account';
import { adminApi, type AProject } from './adminApi';
import { act, fmtAgo, fmtBytes, fmtDate, isoToTs, LoadError, SortTh, useLoad, type Sort } from './common';

type Key = 'name' | 'owner' | 'type' | 'modified' | 'size';

export function ProjectsTab({ query }: { query: URLSearchParams }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [owner, setOwner] = useState<number>(Number(query.get('owner')) || 0);
  const [type, setType] = useState('');
  const [trash, setTrash] = useState<'live' | 'trash'>(query.get('trash') ? 'trash' : 'live');
  const [sort, setSort] = useState<Sort<Key>>({ key: 'modified', dir: -1 });
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setOwner(Number(query.get('owner')) || 0); }, [query.get('owner')]);
  const users = useLoad(() => adminApi.users(false), []);
  const { data, error, loading, reload } = useLoad(() => adminApi.projects({ q: debounced, owner, type, trash: trash === 'trash' }), [debounced, owner, type, trash]);

  const rows = useMemo(() => {
    const arr = data?.projects || [];
    const by: Record<Key, (a: AProject, b: AProject) => number> = {
      name: (a, b) => a.name.localeCompare(b.name, 'ru'),
      owner: (a, b) => (a.owner.name || a.owner.login).localeCompare(b.owner.name || b.owner.login, 'ru') || b.mtime - a.mtime,
      type: (a, b) => (TYPE_NAMES[a.type] || a.type).localeCompare(TYPE_NAMES[b.type] || b.type, 'ru'),
      modified: (a, b) => (isoToTs(a.trashed) || a.mtime) - (isoToTs(b.trashed) || b.mtime),
      size: (a, b) => a.size - b.size,
    };
    return [...arr].sort((a, b) => by[sort.key](a, b) * sort.dir);
  }, [data, sort]);
  const total = rows.reduce((s, p) => s + (p.size || 0), 0);

  const open = (p: AProject) => { location.hash = '#/p/' + encodeURIComponent(p.id); };
  const zip = (p: AProject) => act(async () => {
    ctx.toast(`Архив «${p.name}»…`, 'info', 2000);
    const r = await adminApi.exportZip(p.id);
    if (r.file) download(adminApi.fileUrl(p.id, r.file));
  });
  const toTrash = async (p: AProject) => {
    const ok = await askConfirm({ title: 'Удалить отчёт пользователя?', danger: true, ok: 'В корзину', testid: 'admin-trash-confirm',
      text: `«${p.name}» пользователя ${p.owner.login} будет удалён в корзину администратора. Пользователь увидит в своей корзине, что отчёт удалил администратор, но восстановить или стереть его не сможет. Вернуть - «Корзины» на этой вкладке.` });
    if (ok) void act(() => adminApi.trashProject(p.id), 'Отчёт перемещён в корзину', reload);
  };
  const restore = (p: AProject) => act(() => adminApi.restoreProject(p.owner.id, p.id), `«${p.name}» восстановлен у ${p.owner.login}`, reload);
  const purge = async (p: AProject) => {
    const ok = await askConfirm({ title: 'Удалить навсегда?', danger: true, ok: 'Удалить навсегда', testid: 'admin-purge-confirm',
      text: `«${p.name}» пользователя ${p.owner.login} будет стёрт с диска без возможности восстановления.` });
    if (ok) void act(() => adminApi.purgeProject(p.owner.id, [p.id]), 'Удалено навсегда', reload);
  };
  const isTrash = trash === 'trash';

  return (
    <section className="adm-tab" data-testid="admin-projects">
      <div className="adm-toolbar">
        <Segmented value={trash} onChange={setTrash} testid="projects-mode" options={[
          { id: 'live', label: 'Отчёты', icon: 'file' }, { id: 'trash', label: 'Корзины', icon: 'trash' }]} />
        <div className="search-box"><Icon name="search" size={14} />
          <input className="search" placeholder="Имя, тема, дисциплина, студент, логин" value={q} onChange={(e) => setQ(e.target.value)} data-testid="projects-search" aria-label="Поиск отчёта" />
        </div>
        <select value={owner} onChange={(e) => { setOwner(Number(e.target.value)); if (query.get('owner')) history.replaceState(null, '', '#/admin/projects'); }}
          aria-label="Владелец" data-testid="projects-owner">
          <option value={0}>Все пользователи</option>
          {(users.data?.users || []).map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.login})` : u.login}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Вид работы" data-testid="projects-type">
          <option value="">Любой вид работы</option>
          {Object.entries(TYPE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="topbar-spacer" />
        <Btn icon="refresh" title="Обновить" onClick={() => void reload()} className={loading ? 'is-loading' : ''} />
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="projects-table">
          <thead><tr>
            <SortTh k="name" sort={sort} setSort={setSort}>Отчёт</SortTh>
            <SortTh k="owner" sort={sort} setSort={setSort}>Владелец</SortTh>
            <SortTh k="type" sort={sort} setSort={setSort}>Вид работы</SortTh>
            <SortTh k="modified" sort={sort} setSort={setSort}>{isTrash ? 'Удалён' : 'Изменён'}</SortTh>
            <SortTh k="size" sort={sort} setSort={setSort} className="num">Размер</SortTh>
            <th className="adm-col-act" aria-label="Действия" />
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.owner.id + '/' + p.id} data-testid="admin-project-row" data-owner={p.owner.login} data-name={p.name}>
                <td className="adm-proj">
                  {isTrash ? <span className="adm-proj-name">{p.name}{p.byAdmin && <span className="adm-tag" data-testid="admin-trash-byadmin"
                    title={p.trashedBy ? `удалил ${p.trashedBy}` : undefined}>удалён администратором</span>}</span>
                    : <button type="button" className="adm-proj-name adm-link" onClick={() => open(p)} data-testid="admin-project-open-name">{p.name}</button>}
                  <div className="adm-sub">{p.title ? `«${p.title}»` : <span className="adm-dim">без темы</span>}{p.discipline ? ` · ${p.discipline}` : ''}</div>
                  {p.error && <div className="adm-sub bad">{p.error}</div>}
                </td>
                <td>
                  <div className="adm-owner"><Avatar user={p.owner} size={22} />
                    <span><span className="adm-owner-name">{p.owner.name || p.owner.login}</span>{p.owner.name && <span className="adm-dim"> {p.owner.login}</span>}</span>
                  </div>
                </td>
                <td>{TYPE_NAMES[p.type] || p.type || <span className="adm-dim">—</span>}</td>
                <td title={isTrash ? p.trashed : p.modified}>{isTrash ? fmtDate(isoToTs(p.trashed)) : fmtAgo(p.mtime)}</td>
                <td className="num">{fmtBytes(p.size)}</td>
                <td className="adm-col-act adm-actions">
                  {isTrash ? <>
                    <Btn icon="restore" label="Восстановить" onClick={() => void restore(p)} testid="admin-project-restore" />
                    <Btn icon="close" title="Удалить навсегда" danger onClick={() => void purge(p)} testid="admin-project-purge" />
                  </> : <>
                    <Btn icon="open-in" label="Открыть" onClick={() => open(p)} testid="admin-project-open" />
                    <Btn icon="zip" title="Скачать архив (.zip)" onClick={() => void zip(p)} testid="admin-project-zip" />
                    <Btn icon="trash" title="В корзину" danger onClick={() => void toTrash(p)} testid="admin-project-trash" />
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !rows.length && (
          <div className="adm-empty"><Icon name={isTrash ? 'trash' : 'inbox'} size={26} />
            <div>{debounced || owner || type ? 'Ничего не найдено - измените условия поиска' : isTrash ? 'Корзины пользователей пусты' : 'Отчётов пока нет'}</div></div>
        )}
      </div>
      {rows.length > 0 && <div className="adm-foot">{isTrash ? 'В корзинах' : 'Отчётов'}: {rows.length} · {fmtBytes(total)}</div>}
    </section>
  );
}
